import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Grid, Alert, CircularProgress,
  Typography
} from '@mui/material';
import { all } from 'country-codes-list';
import typography from '../../cssUtils/typography';
import ButtonOne from '../../common/normal-button';
import ButtonTwo from '../../common/dashboard-button';

const STATE_OPTIONS = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands',
  'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];

const COUNTRY_OPTIONS = all().map((c) => c.countryNameEn).sort();

// Options for the State/Country selects; keeps an already-saved value that isn't in the list selectable
const addressOptions = (key, current) => {
  const options = key === 'state' ? STATE_OPTIONS : COUNTRY_OPTIONS;
  return current && !options.includes(current) ? [current, ...options] : options;
};

// Installation Edit Modal Component
export const InstallationEditModal = ({
  open,
  onClose,
  formData,
  onInputChange,
  onAddressChange,
  onUpdate,
  loading = false,
  error = null,
  success = false
}) => {
  const validatePhone = (value) => {
    // Only allow numbers and limit to 15 characters
    return /^\d*$/.test(value) && value.length <= 15;
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ ...typography.h1 }}>Edit Installation Details</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => onClose()}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => onClose()}>
            Installation updated successfully!
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ticket ID"
              value={formData.ticket_code}
              onChange={(e) => onInputChange('ticket_code', e.target.value)}
              variant="outlined"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Customer Name"
              value={formData.customer_name}
              onChange={(e) => onInputChange('customer_name', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="WA Name"
              value={formData.wa_name}
              onChange={(e) => onInputChange('wa_name', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.customer_phone}
              onChange={(e) => {
                if (validatePhone(e.target.value)) {
                  onInputChange('customer_phone', e.target.value);
                }
              }}
              onKeyPress={(e) => {
                if (!/\d/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              inputProps={{
                maxLength: 15,
              }}
              variant="outlined"
              error={formData.customer_phone.length > 0 && !/^\d+$/.test(formData.customer_phone)}
              helperText={
                formData.customer_phone.length > 0 && !/^\d+$/.test(formData.customer_phone)
                  ? 'Phone number should contain only numbers'
                  : ''
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="WA Phone"
              value={formData.wa_number}
              onChange={(e) => onInputChange('wa_number', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Door Type</InputLabel>
              <Select
                value={formData.door_type}
                label="Door Type"
                onChange={(e) => onInputChange('door_type', e.target.value)}
              >
                <MenuItem value="Wooden">Wooden</MenuItem>
                <MenuItem value="Metal">Metal</MenuItem>
                <MenuItem value="Glass">Glass</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Lock Type</InputLabel>
              <Select
                value={formData.lock_type}
                label="Lock Type"
                onChange={(e) => onInputChange('lock_type', e.target.value)}
              >
                <MenuItem value="XL-C 4605">XL-C 4605</MenuItem>
                <MenuItem value="XL-C 4606">XL-C 4606</MenuItem>
                <MenuItem value="XL-C 4607">XL-C 4607</MenuItem>
                <MenuItem value="XL-C 4608">XL-C 4608</MenuItem>
                <MenuItem value="XL-C 4609">XL-C 4609</MenuItem>
                <MenuItem value="XL-C 4610">XL-C 4610</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {formData.lock_type === "Others" && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lock Type - Description"
                value={formData.lock_type_others}
                onChange={(e) => onInputChange('lock_type_others', e.target.value)}
                variant="outlined"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Door Installed</InputLabel>
              <Select
                value={formData.door_installed ? 'Yes' : 'No'}
                label="Door Installed"
                onChange={(e) => onInputChange('door_installed', e.target.value === 'Yes')}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || ''}
                label="Status"
                onChange={(e) => onInputChange('status', e.target.value)}
              >
                <MenuItem value="New">New</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1, ...typography.h1 }}>Address</Typography>
          </Grid>
          
          {['flat', 'street', 'area', 'pincode', 'city', 'state', 'country'].map((key) => (
            <Grid item xs={12} sm={6} key={key}>
              {key === 'state' || key === 'country' ? (
                <FormControl fullWidth>
                  <InputLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</InputLabel>
                  <Select
                    value={formData.address?.[key] || ''}
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    onChange={(e) => onAddressChange(key, e.target.value)}
                  >
                    {addressOptions(key, formData.address?.[key]).map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label={key.charAt(0).toUpperCase() + key.slice(1)}
                  value={formData.address?.[key] || ''}
                  onChange={(e) => onAddressChange(key, e.target.value)}
                  variant="outlined"
                  type={key === 'pincode' ? 'number' : 'text'}
                />
              )}
            </Grid>
          ))}
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => onInputChange('notes', e.target.value)}
              variant="outlined"
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonTwo onClick={onClose} color="error" variant="contained">
          Cancel
        </ButtonTwo>
        <ButtonOne onClick={onUpdate} color="primary" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Update Installation'}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

// Service Edit Modal Component
export const ServiceEditModal = ({
  open,
  onClose,
  formData,
  onInputChange,
  onAddressChange,
  onUpdate,
  loading = false,
  error = null,
  success = false
}) => {
  const validatePhone = (value) => {
    // Only allow numbers and limit to 15 characters
    return /^\d*$/.test(value) && value.length <= 15;
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Service Details</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => onClose()}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => onClose()}>
            Service updated successfully!
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ticket ID"
              value={formData.ticket_code}
              onChange={(e) => onInputChange('ticket_code', e.target.value)}
              variant="outlined"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Lock Model Name"
              value={formData.serial_number}
              onChange={(e) => onInputChange('serial_number', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.customer_phone}
              onChange={(e) => {
                if (validatePhone(e.target.value)) {
                  onInputChange('customer_phone', e.target.value);
                }
              }}
              onKeyPress={(e) => {
                if (!/\d/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              inputProps={{
                maxLength: 15,
              }}
              variant="outlined"
              error={formData.customer_phone.length > 0 && !/^\d+$/.test(formData.customer_phone)}
              helperText={
                formData.customer_phone.length > 0 && !/^\d+$/.test(formData.customer_phone)
                  ? 'Phone number should contain only numbers'
                  : ''
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="WA Phone"
              value={formData.wa_number}
              onChange={(e) => onInputChange('wa_number', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Customer Name"
              value={formData.customer_name}
              onChange={(e) => onInputChange('customer_name', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="WA Name"
              value={formData.wa_name}
              onChange={(e) => onInputChange('wa_name', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Lock Type</InputLabel>
              <Select
                value={formData.lock_type}
                label="Lock Type"
                onChange={(e) => onInputChange('lock_type', e.target.value)}
              >
                <MenuItem value="XL-C 4605">XL-C 4605</MenuItem>
                <MenuItem value="XL-C 4606">XL-C 4606</MenuItem>
                <MenuItem value="XL-C 4607">XL-C 4607</MenuItem>
                <MenuItem value="XL-C 4608">XL-C 4608</MenuItem>
                <MenuItem value="XL-C 4609">XL-C 4609</MenuItem>
                <MenuItem value="XL-C 4610">XL-C 4610</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Service Status</InputLabel>
              <Select
                value={formData.service_status}
                label="Service Status"
                onChange={(e) => onInputChange('service_status', e.target.value)}
              >
                <MenuItem value="New">New</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Urgent">Urgent</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Issue Type</InputLabel>
              <Select
                value={formData.issue_type}
                label="Issue Type"
                onChange={(e) => onInputChange('issue_type', e.target.value)}
              >
                <MenuItem value="Door alignment issue">Door alignment issue</MenuItem>
                <MenuItem value="Lock not working">Lock not working</MenuItem>
                <MenuItem value="Battery issue">Battery Issue</MenuItem>
                <MenuItem value="Key Issues">Key Issues</MenuItem>
                <MenuItem value="Software Update">Software Update</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.issue_description && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Issue Description"
                value={formData.issue_description}
                onChange={(e) => onInputChange('issue_description', e.target.value)}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1, ...typography.h1 }}>Address</Typography>
          </Grid>
          
          {['flat', 'street', 'area', 'pincode', 'city', 'state', 'country'].map((key) => (
            <Grid item xs={12} sm={6} key={key}>
              {key === 'state' || key === 'country' ? (
                <FormControl fullWidth>
                  <InputLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</InputLabel>
                  <Select
                    value={formData.address?.[key] || ''}
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    onChange={(e) => onAddressChange(key, e.target.value)}
                  >
                    {addressOptions(key, formData.address?.[key]).map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label={key.charAt(0).toUpperCase() + key.slice(1)}
                  value={formData.address?.[key] || ''}
                  onChange={(e) => onAddressChange(key, e.target.value)}
                  variant="outlined"
                  type={key === 'pincode' ? 'number' : 'text'}
                />
              )}
            </Grid>
          ))}

          {formData.requirements && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Requirements"
                value={formData.requirements}
                onChange={(e) => onInputChange('requirements', e.target.value)}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error" variant="contained">
          Cancel
        </Button>
        <Button onClick={onUpdate} color="primary" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Update Service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Sales Edit Modal Component
export const SalesEditModal = ({
  open,
  onClose,
  formData,
  onInputChange,
  onAddressChange,
  onFeatureChange,
  onUpdate,
  loading = false,
  error = null,
  success = false
}) => {
  const validatePhone = (value) => {
    // Only allow numbers and limit to 15 characters
    return /^\d*$/.test(value) && value.length <= 15;
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Sales Details</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => onClose()}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => onClose()}>
            Sales updated successfully!
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ticket ID"
              value={formData.ticket_code}
              variant="outlined"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.customer_phone}
              onChange={(e) => {
                if (validatePhone(e.target.value)) {
                  onInputChange('customer_phone', e.target.value);
                }
              }}
              onKeyPress={(e) => {
                if (!/\d/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              inputProps={{
                maxLength: 15,
              }}
              variant="outlined"
              error={formData.customer_phone.length > 0 && !/^\d+$/.test(formData.customer_phone)}
              helperText={
                formData.customer_phone.length > 0 && !/^\d+$/.test(formData.customer_phone)
                  ? 'Phone number should contain only numbers'
                  : ''
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="WA Phone"
              value={formData.wa_number}
              onChange={(e) => onInputChange('wa_number', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Customer Name"
              value={formData.customer_name}
              onChange={(e) => onInputChange('customer_name', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="WA Name"
              value={formData.wa_name}
              onChange={(e) => onInputChange('wa_name', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Door Type</InputLabel>
              <Select
                value={formData.door_type}
                label="Door Type"
                onChange={(e) => onInputChange('door_type', e.target.value)}
              >
                <MenuItem value="Wooden">Wooden</MenuItem>
                <MenuItem value="Metal">Metal</MenuItem>
                <MenuItem value="Glass">Glass</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => {
                let value = parseInt(e.target.value, 10);
                if (isNaN(value)) value = '';
                if (value === '' || (value >= 1 && value <= 100)) {
                  onInputChange('quantity', value);
                }
              }}
              variant="outlined"
              inputProps={{ min: 1, max: 100 }}
              helperText="Enter a value between 1 and 100"
              error={formData.quantity < 1 || formData.quantity > 100}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Sales Status</InputLabel>
              <Select
                value={formData.sales_status}
                label="Sales Status"
                onChange={(e) => onInputChange('sales_status', e.target.value)}
              >
                <MenuItem value="New">New</MenuItem>
                <MenuItem value="Proposal Sent">Proposal Sent</MenuItem>
                <MenuItem value="In Negotiation">In Negotiation</MenuItem>
                <MenuItem value="Closed Won">Closed Won</MenuItem>
                <MenuItem value="Closed Lost">Closed Lost</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1, ...typography.h1 }}>Address</Typography>
          </Grid>
          
          {['flat', 'street', 'area', 'pincode', 'city', 'state', 'country'].map((key) => (
            <Grid item xs={12} sm={6} key={key}>
              {key === 'state' || key === 'country' ? (
                <FormControl fullWidth>
                  <InputLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</InputLabel>
                  <Select
                    value={formData.address?.[key] || ''}
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    onChange={(e) => onAddressChange(key, e.target.value)}
                  >
                    {addressOptions(key, formData.address?.[key]).map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label={key.charAt(0).toUpperCase() + key.slice(1)}
                  value={formData.address?.[key] || ''}
                  onChange={(e) => onAddressChange(key, e.target.value)}
                  variant="outlined"
                  type={key === 'pincode' ? 'number' : 'text'}
                />
              )}
            </Grid>
          ))}

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Features</Typography>
          </Grid>
          {['fingerprint_access', 'rfid_card_access', 'pin_code_access', 'mechanical_key_access', 'mobile_app_access', 'video_access'].map((feature) => (
            <Grid item xs={12} sm={4} key={feature}>
              <FormControl fullWidth>
                <InputLabel>
                  {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </InputLabel>
                <Select
                  value={formData.features?.[feature] ? 'Yes' : 'No'}
                  label={feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  onChange={(e) => onFeatureChange(feature, e.target.value === 'Yes')}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error" variant="contained">
          Cancel
        </Button>
        <Button onClick={onUpdate} color="primary" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Update Sales'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};